import { imageContentTypeForFileName } from "@afar-research/shared/filePreview";

export interface ExpandedFilePreview {
  readonly name: string;
  readonly src: string;
  readonly mimeType?: string;
}

const MAX_FILE_PREVIEW_BYTES = 10 * 1024 * 1024;

export class FilePreviewTooLargeError extends Error {
  constructor() {
    super("This attachment is too large to preview in-app (maximum 10 MB).");
    this.name = "FilePreviewTooLargeError";
  }
}

const TEXT_FILE_EXTENSIONS = new Set([
  "css",
  "csv",
  "graphql",
  "htm",
  "html",
  "ini",
  "js",
  "json",
  "jsx",
  "log",
  "md",
  "mdx",
  "py",
  "sql",
  "svg",
  "toml",
  "ts",
  "tsx",
  "txt",
  "xml",
  "yaml",
  "yml",
]);

/**
 * `application/octet-stream` is what a stored upload is served as and what
 * older records recorded for a picked file. It means "unknown", not "binary
 * blob to download", so it never settles what the preview should be.
 */
function declaredType(mimeType: string | undefined): string | undefined {
  const normalized = mimeType?.toLowerCase();
  return normalized && normalized !== "application/octet-stream" ? normalized : undefined;
}

/**
 * Whether this attachment should be shown as a picture.
 *
 * A blob without an image type renders as a blank pane, so an image has to be
 * recognized before it reaches the document preview. The file's own name
 * answers for anything the record could not type — which is every attachment
 * picked before live references existed.
 */
export function isImageFilePreview(
  preview: Pick<ExpandedFilePreview, "name" | "mimeType">,
): boolean {
  const declared = declaredType(preview.mimeType);
  return declared
    ? declared.startsWith("image/")
    : imageContentTypeForFileName(preview.name) !== undefined;
}

/**
 * The type to give the preview blob: what the record says, else what the
 * response says, else what the file's name implies for an image.
 */
export function filePreviewContentType(
  preview: Pick<ExpandedFilePreview, "name" | "mimeType">,
  responseContentType: string | null,
): string {
  const declared = declaredType(preview.mimeType);
  if (declared) return declared;
  const fromResponse = declaredType(responseContentType ?? undefined);
  if (fromResponse) return fromResponse;
  return imageContentTypeForFileName(preview.name) ?? responseContentType ?? "";
}

export function isTextFilePreview(
  preview: Pick<ExpandedFilePreview, "name" | "mimeType">,
): boolean {
  const mimeType = preview.mimeType?.toLowerCase();
  if (mimeType?.startsWith("text/")) return true;
  if (mimeType && ["application/json", "application/xml", "application/yaml"].includes(mimeType)) {
    return true;
  }
  const extension = preview.name.split(".").at(-1)?.toLowerCase();
  return extension !== undefined && TEXT_FILE_EXTENSIONS.has(extension);
}

export async function readBoundedFilePreview(
  response: Response,
  maximumBytes = MAX_FILE_PREVIEW_BYTES,
): Promise<Uint8Array> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > maximumBytes) {
    throw new FilePreviewTooLargeError();
  }

  if (!response.body) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.byteLength > maximumBytes) throw new FilePreviewTooLargeError();
    return bytes;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let byteLength = 0;
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      byteLength += result.value.byteLength;
      if (byteLength > maximumBytes) {
        await reader.cancel();
        throw new FilePreviewTooLargeError();
      }
      chunks.push(result.value);
    }
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}
